import React, { useState, useEffect } from "react";
import { Offcanvas, Form, Button, Row, Col, Card, Modal, Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { userService } from '../services/userService';
import { accountService } from '../services/accountService';
import { urlService } from '../services/urlService';
import urlTrackingIcon from "../assets/images/icons/url-link.svg";
import individualTrackingIcon from "../assets/images/mouse-clicker.png";
import Celebration from "../assets/images/icons/Celebration.png";

const SingleUrlOffCanvas = ({ show, onHide }) => {
  const [formData, setFormData] = useState({
    campaignName: "",
    longUrl: "",
    locale: "",
    language: "",
    senderId: "",
    indivId: "",
    mobileNo: "",
    urlTracking: false,
    individualTracking: false,
    domain: ""
  });

  const [domains, setDomains] = useState([]);
  const [senderIds, setSenderIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showIndividualForm, setShowIndividualForm] = useState(false);
  const [shortUrl, setShortUrl] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [locales, setLocales] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [initialUrlTracking, setInitialUrlTracking] = useState(false);
  const [initialIndividualTracking, setInitialIndividualTracking] = useState(false);

  useEffect(() => {
    if (show) {
      getUserDetails();
      fetchLocales();
    }
  }, [show]);

  const getUserDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        
        const userData = await userService.getUserByUsername(decoded.sub);

        const accountConfig = await accountService.getAccountConfigById(userData.accountId);
        
        setSenderIds(accountConfig.senderIds || []);

        try {
          const domainObjs = await accountService.getDomainsByAccount(userData.accountId);
          setDomains(Array.isArray(domainObjs) ? domainObjs : []);
        } catch (e) {
          setDomains(accountConfig.domains?.map((domain, index) => ({
            id: index,
            domain: domain,
            accountId: userData.accountId,
            defaultDomain: domain === accountConfig.defaultDomain,
            assigned: true
          })) || []);
        }

        setInitialUrlTracking(accountConfig.urlTracking);
        setInitialIndividualTracking(accountConfig.individualTracking);
        
        setShowForm(accountConfig.urlTracking);
        setShowIndividualForm(accountConfig.individualTracking);

        if (accountConfig.defaultDomain) {
          setFormData(prev => ({
            ...prev,
            domain: accountConfig.defaultDomain,
            urlTracking: accountConfig.urlTracking,
            individualTracking: accountConfig.individualTracking
          }));
        }

        setUserRole(userData.role);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch account configuration. Please try again.');
    }
  };

  const fetchLocales = async () => {
    try {
      const locales = await userService.getLocales();
      setLocales(locales);
    } catch (error) {
      console.error('Error fetching locales:', error);
      toast.error('Failed to fetch locales');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await urlService.shortenUrl({
        ...formData,
        language: formData.language
      });
      setShortUrl(data.shortUrl);
      setShowSuccessModal(true);
      toast.success('URL shortened successfully!');
    } catch (error) {
      console.error('Error shortening URL:', error);
      toast.error('Failed to shorten URL. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shortUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('URL copied to clipboard!');
  };

  const resetForm = () => {
    setFormData({
      campaignName: "",
      longUrl: "",
      locale: "",
      language: "",
      senderId: "",
      indivId: "",
      mobileNo: "",
      urlTracking: false,
      individualTracking: false,
      domain: ""
    });
    setShowForm(false);
    setShowIndividualForm(false);
    setShortUrl("");
    setShowSuccessModal(false);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement="end">
        <Offcanvas.Header closeButton className="border-bottom bg-white">
          <Offcanvas.Title className="fs-18 fw-600" style={{fontSize:'16px'}}>Single URL Shortening</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12">Sender ID</Form.Label>
                  <Form.Select
                    name="senderId"
                    value={formData.senderId}
                    onChange={handleInputChange}
                    className="placeholder-text"
                  >
                    <option value="">Sender ID</option>
                    {senderIds.map((senderId, index) => (
                      <option key={index} value={senderId}>
                        {senderId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-12">Domain</Form.Label>
                  <Form.Select
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="placeholder-text"
                  >
                    <option value="">Domain</option>
                    {domains.map((domain, index) => (
                      <option 
                        key={index} 
                        value={domain.domain}
                        className={domain.defaultDomain ? 'fw-bold' : ''}
                      >
                        {domain.domain} {domain.defaultDomain ? '(Default)' : ''} {`- ${domain.assigned ? 'Verified' : 'Not Verified'}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fs-12">Long URL</Form.Label>
              <Form.Control
                type="url"
                name="longUrl"
                value={formData.longUrl}
                onChange={handleInputChange}
                placeholder="eg. https://my-long-url.com"
                required
              />
              <Form.Text className="text-242D35 fs-12">
                <strong className="GeneralSansMedium">Note</strong>: Long URL must include the https://
              </Form.Text>
            </Form.Group>

            <div className="d-flex align-items-center gap-4 mb-3">
              <div className="d-flex align-items-center">
                {userRole === "SUPERADMIN" || initialUrlTracking ? (
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={showForm}
                      onChange={(e) => {
                        setShowForm(e.target.checked);
                        setFormData(prev => ({
                          ...prev, 
                          urlTracking: e.target.checked
                        }));
                      }}
                      disabled={userRole !== "SUPERADMIN" && !initialUrlTracking}
                      style={{ fontSize: "13px" }}
                    />
                    <img src={urlTrackingIcon} alt="urlTrackingIcon" className="mx-2" style={{ width: "12px", height: "12px" }} />
                    <span style={{ fontSize: "13px" }}>URL Tracking</span>
                  </div>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>This feature is currently disabled. Please contact the system administrator.</Tooltip>}
                  >
                    <div className="d-flex align-items-center" style={{ cursor: "not-allowed" }}>
                      {/* <div className="position-relative">
                        <Form.Check
                          type="checkbox"
                          checked={false}
                          onChange={() => {}}
                          disabled
                          style={{ 
                            fontSize: "13px",
                            opacity: 0.5,
                            cursor: "not-allowed"
                          }}
                        />
                        <FaTimes 
                          className="position-absolute" 
                          style={{
                            color: "#dc3545",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: "16px"
                          }}
                        />
                      </div> */}
                      <img src={urlTrackingIcon} alt="urlTrackingIcon" className="mx-2" style={{ width: "12px", height: "12px" }} />
                      <span style={{ 
                        fontSize: "13px",
                        cursor: 'no-drop',
                        textDecoration: 'line-through',
                        opacity: '0.4'
                      }}>URL Tracking</span>
                    </div>
                  </OverlayTrigger>
                )}
              </div>

              <div className="d-flex align-items-center">
                {userRole === "SUPERADMIN" || initialIndividualTracking ? (
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={showIndividualForm}
                      onChange={(e) => {
                        setShowIndividualForm(e.target.checked);
                        setFormData(prev => ({
                          ...prev, 
                          individualTracking: e.target.checked
                        }));
                      }}
                      disabled={userRole !== "SUPERADMIN" && !initialIndividualTracking}
                      style={{ fontSize: "13px" }}
                    />
                    <img src={individualTrackingIcon} alt="individualTrackingIcon" className="mx-2" style={{ width: "12px", height: "12px" }} />
                    <span style={{ fontSize: "13px" }}>Individual Tracking</span>
                  </div>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>This feature is currently disabled. Please contact the system administrator.</Tooltip>}
                  >
                    <div className="d-flex align-items-center" style={{ cursor: "not-allowed" }}>
                      {/* <div className="position-relative">
                        <Form.Check
                          type="checkbox"
                          checked={false}
                          onChange={() => {}}
                          disabled
                          style={{ 
                            fontSize: "13px",
                            opacity: 0.5,
                            cursor: "not-allowed"
                          }}
                        />
                        <FaTimes 
                          className="position-absolute" 
                          style={{
                            color: "#dc3545",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: "16px"
                          }}
                        />
                      </div> */}
                      <img src={individualTrackingIcon} alt="individualTrackingIcon" className="mx-2" style={{ width: "12px", height: "12px" }} />
                      <span style={{ 
                        fontSize: "13px",
                        cursor: 'no-drop',
                        textDecoration: 'line-through',
                        opacity: '0.4'
                      }}>Individual Tracking</span>
                    </div>
                  </OverlayTrigger>
                )}
              </div>
            </div>

            {(showForm || showIndividualForm) && (
              <Row className="mt-4">
                <Col md={6} className="d-flex">
                  <Card className={`br-radius-12 ${!showForm ? 'opacity-50' : ''}`}>
                    <Card.Body className="p-3 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <img src={urlTrackingIcon} alt="urlTrackingIcon" className="me-2" style={{ width: "16px", height: "16px" }} />
                        <h6 className="mb-0 fs-14">URL Tracking</h6>
                      </div>
                      <Form.Group className="mb-3">
                        <Form.Label className="fs-12">Campaign name</Form.Label>
                        <Form.Control
                          type="text"
                          name="campaignName"
                          value={formData.campaignName}
                          onChange={handleInputChange}
                          placeholder="Campaign Name"
                          disabled={!showForm}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label className="fs-12">Locale</Form.Label>
                        <Form.Select
                          name="locale"
                          value={formData.locale}
                          onChange={handleInputChange}
                          disabled={!showForm}
                        >
                          <option value="">Locale</option>
                          {locales.map((locale) => (
                            <option key={locale.id} value={locale.locale}>
                              {locale.region}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="fs-12">Language</Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          disabled={!showForm}
                        >
                          <option value="">Language</option>
                          {[...new Set(locales.map(locale => locale.language))].map((language) => (
                            <option key={language} value={language.toUpperCase()}>
                              {language}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className={`br-radius-12 ${!showIndividualForm ? 'opacity-50' : ''}`}>
                    <Card.Body className="p-3 d-flex flex-column">
                      <div className="d-flex align-items-center mb-3">
                        <img src={individualTrackingIcon} alt="individualTrackingIcon" className="me-2" style={{ width: "16px", height: "16px" }} />
                        <h6 className="mb-0 fs-14">Individual Tracking</h6>
                      </div>
                      <Form.Group className="mb-3">
                        <Form.Label className="fs-12">Individual ID</Form.Label>
                        <Form.Control
                          type="text"
                          name="indivId"
                          value={formData.indivId}
                          onChange={handleInputChange}
                          placeholder="Individual ID"
                          disabled={!showIndividualForm}
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="fs-12">Mobile Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="mobileNo"
                          value={formData.mobileNo}
                          onChange={handleInputChange}
                          placeholder="Mobile Number"
                          disabled={!showIndividualForm}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button variant="outline-secondary" className="me-2" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="success" 
                className="fw-600"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  'Generate'
                )}
              </Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title>
            <span className="fs-15 GeneralSansSemibold">Your Link is Ready!</span> 
            <Image className="ms-2" width={28} src={Celebration} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="fs-12 mb-2">Copy the link below to share it or choose a platform to share it to...</p>
          <div className="bg-f1f1f1 br-radius-12 p-3 pt-4 pb-4">
            <Form.Group className="mb-3">
              <div className="d-flex" style={{ border: '1px solid #EAECEE' }}>
                <Form.Control 
                  className="group-warning-btn GeneralSansSemibold rounded-0 fs-14 text-center border border-warning" 
                  type="text" 
                  value={shortUrl} 
                  readOnly 
                />
                <Button 
                  variant="danger" 
                  onClick={handleCopy} 
                  style={{ fontFamily: "General Sans Medium", paddingLeft: '20px', paddingRight: '20px' }} 
                  className="bg-FF6347 GeneralSansSemibold fs-14 border border-warning rounded-0"
                >
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>
            </Form.Group>
            <Form.Text className="text-242D35 fs-12">
              <div className="d-flex">
                <strong className="GeneralSansMedium text-nowrap me-2">Long URL :</strong>
                <span className="long-url" title={formData.longUrl}>{formData.longUrl}</span>
              </div>
            </Form.Text>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SingleUrlOffCanvas;